import { computed, type SetupContext } from 'vue';
import { formatMoney } from '@/domain/selection';
import type {
  PhotoSelectionScreenEvents,
  PhotoSelectionScreenProps,
} from './types';

export function usePhotoSelectionController(
  props: PhotoSelectionScreenProps,
  emit: SetupContext<PhotoSelectionScreenEvents>['emit']
) {
  const groupCount = computed(() =>
    props.session.groupPhotos.reduce(
      (sum, photo) => sum + (props.selection[photo.id] ?? 0),
      0
    )
  );
  const portraitCount = computed(() =>
    props.session.portraits.reduce(
      (sum, photo) => sum + (props.selection[photo.id] ?? 0),
      0
    )
  );
  const amountLabel = computed(() =>
    props.quote.amountDueCents > 0
      ? formatMoney(props.quote.amountDueCents, props.quote.currency)
      : formatMoney(0, props.quote.currency)
  );
  const groupUnitPriceLabel = computed(() =>
    formatMoney(props.priceConfig.groupPhotoCents, props.priceConfig.currency)
  );
  const portraitUnitPriceLabel = computed(() =>
    formatMoney(props.priceConfig.portraitCents, props.priceConfig.currency)
  );
  const isPartialVoucherCoverage = computed(
    () =>
      props.quote.prepaidAppliedCount > 0 &&
      props.quote.prepaidAppliedCount < props.quote.totalSelected
  );
  const partialCoverageParams = computed(() => ({
    covered: props.quote.prepaidAppliedCount,
    total: props.quote.totalSelected,
    due: formatMoney(props.quote.amountDueCents, props.quote.currency),
  }));

  function quantity(photoId: string): number {
    return props.selection[photoId] ?? 0;
  }

  return {
    groupCount,
    portraitCount,
    amountLabel,
    groupUnitPriceLabel,
    portraitUnitPriceLabel,
    isPartialVoucherCoverage,
    partialCoverageParams,
    quantity,
    increase: (photoId: string) => emit('changeQuantity', photoId, 1),
    decrease: (photoId: string) => emit('changeQuantity', photoId, -1),
    back: () => emit('back'),
    help: () => emit('help'),
    continue: () => emit('continue'),
  };
}
